import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AuthService from '../../services/AuthService';
import CrmWorkflowService from '../../services/CrmWorkflowService';
import { DEAL_STAGES, getDealStage } from '../../constants/crmWorkflowConstants';

function rowDealStage(d) {
  return d?.stage ?? d?.Stage ?? '';
}

function stageRank(stageId) {
  const i = DEAL_STAGES.findIndex((s) => s.id === stageId);
  return i >= 0 ? i : -1;
}

/** Best stage to show in list: most advanced open deal, else most advanced among all deals. */
function primaryDealStageSummary(deals) {
  if (!deals?.length) return null;
  const isTerminal = (s) => s === 'ClosedWon' || s === 'ClosedLost';
  const open = deals.filter((d) => !isTerminal(rowDealStage(d)));
  const pool = open.length ? open : deals;
  let best = pool[0];
  let bestR = stageRank(rowDealStage(best));
  for (let i = 1; i < pool.length; i++) {
    const r = stageRank(rowDealStage(pool[i]));
    if (r > bestR) {
      best = pool[i];
      bestR = r;
    }
  }
  return { meta: getDealStage(rowDealStage(best)), dealCount: deals.length };
}

/** Tenant-wide list of qualified buyers (converted from leads). */
export default function Buyers() {
  const navigate = useNavigate();
  const clientId = AuthService.getLoggedInClientId();
  const [loading, setLoading] = useState(true);
  const [buyers, setBuyers] = useState([]);
  const [clientDeals, setClientDeals] = useState([]);
  const [q, setQ] = useState('');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!clientId) {
        setLoading(false);
        return;
      }
      setLoading(true);
      try {
        const [list, deals] = await Promise.all([
          CrmWorkflowService.getBuyersByClient(clientId),
          CrmWorkflowService.getDealsByClient(clientId).catch(() => []),
        ]);
        if (!cancelled) {
          setBuyers(list);
          setClientDeals(Array.isArray(deals) ? deals : []);
        }
      } catch (e) {
        console.error(e);
        if (!cancelled) {
          setBuyers([]);
          setClientDeals([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [clientId]);

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return buyers;
    return buyers.filter((b) => {
      const hay = [
        b.firstName,
        b.lastName,
        b.email,
        b.phone,
        b.sourceLeadCode,
        b.assignedUserName,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return hay.includes(s);
    });
  }, [buyers, q]);

  const dealsByBuyerId = useMemo(() => {
    const m = new Map();
    for (const d of clientDeals) {
      const bid = d.buyerId ?? d.BuyerId;
      if (bid == null) continue;
      if (!m.has(bid)) m.set(bid, []);
      m.get(bid).push(d);
    }
    return m;
  }, [clientDeals]);

  if (!clientId) {
    return (
      <div className="container-fluid py-4">
        <p className="text-muted">Sign in with a tenant account to manage buyers.</p>
      </div>
    );
  }

  return (
    <div style={{ padding: '8px 8px 32px' }}>
      <div className="d-flex flex-wrap justify-content-between align-items-center gap-3 mb-4">
        <div className="d-flex align-items-center gap-3">
          <div
            style={{
              width: 48,
              height: 48,
              borderRadius: 12,
              background: 'linear-gradient(135deg, #2dce89 0%, #1a9e66 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#fff',
              fontSize: 20,
              boxShadow: '0 4px 12px rgba(45,206,137,0.35)',
            }}
          >
            <i className="fas fa-user-check" />
          </div>
          <div>
            <h4 className="mb-0" style={{ fontWeight: 700, color: '#344767' }}>
              Buyers
            </h4>
            <p className="mb-0 text-muted small">
              Serious customers — separate from raw inquiries. Track lifetime value, repeat deals, and assignments.
            </p>
          </div>
        </div>
        <div className="d-flex flex-wrap gap-2 align-items-center">
          <input
            type="search"
            className="form-control"
            style={{ minWidth: 220, maxWidth: 320 }}
            placeholder="Search name, phone, lead code…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
        </div>
      </div>

      <div className="card shadow-sm border-0" style={{ borderRadius: 16 }}>
        <div className="card-body p-0">
          {loading ? (
            <div className="text-center py-5 text-muted">
              <i className="fas fa-circle-notch fa-spin me-2" />
              Loading buyers…
            </div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-5 px-3">
              <i className="fas fa-user-plus fa-3x text-muted mb-3 opacity-50 d-block" />
              <p className="fw-semibold text-secondary mb-1">No buyers yet</p>
              <p className="text-muted small mb-0 mx-auto" style={{ maxWidth: 520 }}>
                Convert qualified leads from <strong>Assigned Leads</strong> → tab <strong>Properties &amp; Deals</strong> →{' '}
                <strong>Convert lead → buyer</strong>. Buyers then appear here as your long-term sales records.
              </p>
            </div>
          ) : (
            <div className="table-responsive">
              <table className="table table-hover align-middle mb-0" style={{ fontSize: '14px' }}>
                <thead className="table-light">
                  <tr className="text-uppercase small text-muted" style={{ fontSize: '11px', letterSpacing: '0.06em' }}>
                    <th className="ps-4 py-3">Buyer</th>
                    <th className="py-3">Contact</th>
                    <th className="py-3">Budget</th>
                    <th
                      className="py-3"
                      title="Pipeline stage of this buyer’s deals (most advanced open transaction)"
                    >
                      Deal stage
                    </th>
                    <th className="py-3">Source lead</th>
                    <th className="py-3">Owner</th>
                    <th className="py-3">Since</th>
                    <th className="pe-4 py-3 text-end">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((b) => {
                    const name = [b.firstName, b.lastName].filter(Boolean).join(' ') || '—';
                    const budget =
                      b.budgetMin != null || b.budgetMax != null
                        ? `${b.budgetMin != null ? Number(b.budgetMin).toLocaleString('en-IN') : '—'} – ${b.budgetMax != null ? Number(b.budgetMax).toLocaleString('en-IN') : '—'}`
                        : '—';
                    const since =
                      b.createdOn && new Date(b.createdOn).getFullYear() > 1
                        ? new Date(b.createdOn).toLocaleDateString('en-IN', {
                            day: '2-digit',
                            month: 'short',
                            year: 'numeric',
                          })
                        : '—';
                    const buyerDeals = dealsByBuyerId.get(b.id) ?? [];
                    const stageSum = primaryDealStageSummary(buyerDeals);
                    return (
                      <tr key={b.id}>
                        <td className="ps-4 py-3 fw-semibold text-dark">{name}</td>
                        <td className="py-3">
                          <div>{b.phone || '—'}</div>
                          <div className="small text-muted">{b.email || ''}</div>
                        </td>
                        <td className="py-3">{budget}</td>
                        <td className="py-3">
                          {stageSum ? (
                            <div>
                              <span
                                className="badge rounded-pill"
                                style={{
                                  background: stageSum.meta.color + '22',
                                  color: stageSum.meta.color,
                                  fontWeight: 700,
                                  fontSize: '12px',
                                }}
                              >
                                {stageSum.meta.label}
                              </span>
                              {stageSum.dealCount > 1 ? (
                                <div className="small text-muted mt-1">{stageSum.dealCount} deals</div>
                              ) : null}
                            </div>
                          ) : (
                            <span className="text-muted small">No deal yet</span>
                          )}
                        </td>
                        <td className="py-3">
                          {b.sourceLeadCode ? (
                            <span className="badge bg-light text-dark border">{b.sourceLeadCode}</span>
                          ) : (
                            <span className="text-muted">#{b.sourceLeadId}</span>
                          )}
                        </td>
                        <td className="py-3">{b.assignedUserName || <span className="text-muted">—</span>}</td>
                        <td className="py-3 text-muted small">{since}</td>
                        <td className="pe-4 py-3 text-end">
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-dark rounded-pill px-3"
                            onClick={() => navigate(`/buyers/${b.id}`)}
                          >
                            Open profile
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
